import { Button, Container, ContainerSize, Gap, Heading, Nav, Navbar, Stack, StackDirection, Text } from '@rebootui';
import { Link } from "react-router-dom";
import CodeSnippet from "../../../components/controls/CodeSnippet";
import CodePreview from "../../../components/controls/CodePreview";
import CodeHighlighter from "../../../components/controls/CodeHighlighter";
type NavbarProps = {};

const NavbarPage = ({ }: NavbarProps) => {
    return (
        <Container size={ContainerSize.Fluid}>
            <Heading el="h1" className="fw-semibold mb-3 ls-1">
                Navbar
            </Heading>
            <Text el="p" className="mb-4">
                Documentation and examples for Bootstrap's powerful, responsive navigation header, the navbar. Includes support for branding, navigation, and more, including support for our collapse plugin.
            </Text>

            {/* Nav */}
            <Text className="mb-4">
                Here’s an example of all the sub-components included in a responsive light-themed navbar that automatically collapses at the lg (large) breakpoint.
            </Text>
            <Text el="p" className="mb-4">
                These previews use <Text el="code">Nav.Link as=&#123;Link&#125;</Text> from <Text el="code">react-router-dom</Text> so route changes stay client-side. Without <Text el="code">as</Text>, <Text el="code">Nav.Link</Text> renders a regular anchor and the browser performs a full navigation. See the <Link to="/docs/component/nav" className="link-primary">Nav</Link> page for the same pattern in prose.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Navbar className="navbar-expand-lg bg-body-tertiary rounded">
                        <Container size={ContainerSize.Fluid}>
                            <Navbar.Brand>Navbar</Navbar.Brand>
                            <Navbar.Toggle />
                            <Navbar.Collapse>
                                <Nav className="me-auto mb-2 mb-lg-0">
                                    <Nav.Item>
                                        <Nav.Link as={Link} to="#" active>
                                            Home
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link as={Link} to="/">
                                            Features
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link as={Link} to="/">
                                            Pricing
                                        </Nav.Link>
                                    </Nav.Item>
                                    <Nav.Item>
                                        <Nav.Link as={Link} to="/" disabled>
                                            Disabled
                                        </Nav.Link>
                                    </Nav.Item>
                                </Nav>
                                <form className="d-flex" role="search">
                                    <input className="form-control me-2" type="search" placeholder="Search" aria-label="Search" />
                                    <Button className="btn-outline-success">Search</Button>
                                </form>
                            </Navbar.Collapse>
                        </Container>
                    </Navbar>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {navCode}
                </CodeHighlighter>
            </CodeSnippet>


            {/* Brand */}
            <Heading el="h2" className="fw-medium fs-3 mt-4 mb-3">
                Brand
            </Heading>
            <Text className="mb-4">
                The <Text el="strong">Navbar.Brand</Text> can be applied to most elements, but an anchor works best, as some elements might require utility classes or custom styles.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Navbar className="bg-body-tertiary rounded">
                        <Container size={ContainerSize.Fluid}>
                            <Navbar.Brand>Navbar</Navbar.Brand>
                        </Container>
                    </Navbar>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {brandCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Brand Image */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Image
            </Heading>
            <Text className="mb-4">
                You can replace the text within the <Text el="strong">Navbar.Brand</Text> with an image.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Navbar className="bg-body-tertiary">
                        <Container size={ContainerSize.Fluid}>
                            <Navbar.Brand>
                                <img src="/bootstrap-logo.svg" alt="Logo" width="30" height="24" className="d-inline-block align-text-top me-2" />
                            </Navbar.Brand>
                        </Container>
                    </Navbar>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {imageCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Image and text */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Image and text
            </Heading>
            <Text className="mb-4">
                You can also make use of some additional utilities to add an image and text at the same time.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Navbar className="bg-body-tertiary">
                        <Container size={ContainerSize.Fluid}>
                            <Navbar.Brand>
                                <img src="/bootstrap-logo.svg" alt="Logo" width="30" height="24" className="d-inline-block align-text-top me-2" />
                                Bootstrap
                            </Navbar.Brand>
                        </Container>
                    </Navbar>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {imageTextCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Forms */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Forms
            </Heading>
            <Text className="mb-4">
                Place various form controls and components within a navbar.
            </Text>
            <CodeSnippet>
                <CodePreview>
                    <Navbar className="bg-body-tertiary">
                        <Container size={ContainerSize.Fluid}>
                            <form className="d-flex" role="search">
                                <input className="form-control me-2" type="search" placeholder="Search" aria-label="Search" />
                                <Button className="btn-outline-success">Search</Button>
                            </form>
                        </Container>
                    </Navbar>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {formsCode}
                </CodeHighlighter>
            </CodeSnippet>

            {/* Color schemes */}
            <Heading el="h2" className="fw-medium fs-3 mt-5 mb-3">
                Color schemes
            </Heading>
            <Text className="mb-4">
                Theming the navbar has never been easier thanks to the combination of theming classes and <Text el="strong">background-color</Text> utilities. Set <Text el="strong">data-bs-theme="dark"</Text> on the navbar for dark mode, or <Text el="strong">data-bs-theme="light"</Text> for light mode.
            </Text>

            <CodeSnippet>
                <CodePreview>
                    <Stack direction={StackDirection.Column} gap={Gap.Gap3}>
                        <Navbar className="navbar-expand-lg bg-body-tertiary rounded" data-bs-theme="dark">
                            <Container size={ContainerSize.Fluid}>
                                <Navbar.Brand>Navbar</Navbar.Brand>
                                <Navbar.Toggle />
                                <Navbar.Collapse>
                                    <Nav className="me-auto mb-2 mb-lg-0">
                                        <Nav.Item>
                                            <Nav.Link as={Link} to="#" active>
                                                Home
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link as={Link} to="/">
                                                Features
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link as={Link} to="/">
                                                Pricing
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link as={Link} to="/" disabled>
                                                Disabled
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                    <form className="d-flex" role="search">
                                        <input className="form-control me-2" type="search" placeholder="Search" aria-label="Search" />
                                        <Button className="btn-outline-success">Search</Button>
                                    </form>
                                </Navbar.Collapse>
                            </Container>
                        </Navbar>
                        <Navbar className="navbar-expand-lg bg-primary rounded" data-bs-theme="dark">
                            <Container size={ContainerSize.Fluid}>
                                <Navbar.Brand>Navbar</Navbar.Brand>
                                <Navbar.Toggle />
                                <Navbar.Collapse>
                                    <Nav className="me-auto mb-2 mb-lg-0">
                                        <Nav.Item>
                                            <Nav.Link as={Link} to="#" active>
                                                Home
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link as={Link} to="/">
                                                Features
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link as={Link} to="/">
                                                Pricing
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link as={Link} to="/" disabled>
                                                Disabled
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                    <form className="d-flex" role="search">
                                        <input className="form-control me-2" type="search" placeholder="Search" aria-label="Search" />
                                        <Button className="btn-outline-success">Search</Button>
                                    </form>
                                </Navbar.Collapse>
                            </Container>
                        </Navbar>
                        <Navbar className="navbar-expand-lg bg-primary-subtle rounded" data-bs-theme="light">
                            <Container size={ContainerSize.Fluid}>
                                <Navbar.Brand>Navbar</Navbar.Brand>
                                <Navbar.Toggle />
                                <Navbar.Collapse>
                                    <Nav className="me-auto mb-2 mb-lg-0">
                                        <Nav.Item>
                                            <Nav.Link as={Link} to="#" active>
                                                Home
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link as={Link} to="/">
                                                Features
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link as={Link} to="/">
                                                Pricing
                                            </Nav.Link>
                                        </Nav.Item>
                                        <Nav.Item>
                                            <Nav.Link as={Link} to="/" disabled>
                                                Disabled
                                            </Nav.Link>
                                        </Nav.Item>
                                    </Nav>
                                    <form className="d-flex" role="search">
                                        <input className="form-control me-2" type="search" placeholder="Search" aria-label="Search" />
                                        <Button className="btn-outline-success">Search</Button>
                                    </form>
                                </Navbar.Collapse>
                            </Container>
                        </Navbar>
                    </Stack>
                </CodePreview>
                <CodeHighlighter language="tsx">
                    {darkCode}
                </CodeHighlighter>
            </CodeSnippet>
        </Container>
    );
}

export default NavbarPage;

// Code snippets
const brandCode = `<Navbar className="bg-body-tertiary">
    <Container size={ContainerSize.Fluid}>
        <Navbar.Brand>Navbar</Navbar.Brand>
    </Container>
</Navbar>`;

const imageCode = `<Navbar className="bg-body-tertiary">
    <Container size={ContainerSize.Fluid}>
        <Navbar.Brand>
            <img src="/bootstrap-logo.svg" alt="Logo" width="30" height="24" 
                 className="d-inline-block align-text-top" />
        </Navbar.Brand>
    </Container>
</Navbar>`;

const imageTextCode = `<Navbar className="bg-body-tertiary">
    <Container size={ContainerSize.Fluid}>
        <Navbar.Brand>
            <img src="/bootstrap-logo.svg" alt="Logo" width="30" height="24" 
                 className="d-inline-block align-text-top me-2" />
            Bootstrap
        </Navbar.Brand>
    </Container>
</Navbar>`;

const navCode = `import { Link } from "react-router-dom";

<Navbar className="navbar-expand-lg bg-body-tertiary rounded">
    <Container size={ContainerSize.Fluid}>
        <Navbar.Brand>Navbar</Navbar.Brand>
        <Navbar.Toggle />
        <Navbar.Collapse>
            <Nav className="me-auto mb-2 mb-lg-0">
                <Nav.Item>
                    <Nav.Link as={Link} to="#" active>Home</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link as={Link} to="/">Features</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link as={Link} to="/">Pricing</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                    <Nav.Link as={Link} to="/" disabled>Disabled</Nav.Link>
                </Nav.Item>
            </Nav>
            <form className="d-flex" role="search">
                <input className="form-control me-2" type="search" 
                       placeholder="Search" aria-label="Search" />
                <Button className="btn-outline-success">
                    Search
                </Button>
            </form>
        </Navbar.Collapse>
    </Container>
</Navbar>`;

const formsCode = `<Navbar className="bg-body-tertiary">
    <Container size={ContainerSize.Fluid}>
        <form className="d-flex" role="search">
            <input className="form-control me-2" type="search" 
                   placeholder="Search" aria-label="Search" />
            <Button className="btn-outline-success">
                Search
            </Button>
        </form>
    </Container>
</Navbar>`;

const darkCode = `<Navbar className="bg-dark" data-bs-theme="dark">
    ...
</Navbar>

<Navbar className="bg-primary" data-bs-theme="dark">
   ...
</Navbar>

<Navbar className="bg-primary-subtle" data-bs-theme="light">
   ...
</Navbar>
`;









